<template>
  <div class="page-content">
    <OrganismsLoading v-show="isLoading" />
    <PagePartsSet v-if="!isLoading" :page-parts="pagePartsRef" :child-parts-ids="childPartsIds" />
  </div>
</template>

<script setup lang="ts">
import type { PagePartsSetType } from "~/types/page"
import { usePublicProductCategoryFindOneQuery } from "~/gql/urql.generated"
import { ProductCategory, PublicProductCategoryFindOneQueryVariables } from "~/types/type.generated"
import { useSocial } from "~/composables/useSocial"
import { createArticleJsonLD, useCustomMeta } from "~/composables/meta"
import { ScriptPropertySrc, ScriptPropertyText, MetaCustomType } from "~/types/meta"
import { ApiPageOneAbsoluteUrlResponsePage } from "~/types/server/api/page"

definePageMeta({ layout: "default-with-extended-footer" })

const route = useRoute()
const customMeta = useCustomMeta()
const pageRef = ref<ApiPageOneAbsoluteUrlResponsePage>()
const pagePartsRef = ref<PagePartsSetType[]>([])
const childPartsIds = ref<string[]>([])

const isLoading = useState<boolean>(() => true)
const { fetchWithHandleRedirect } = usePageOneApi()
const absoluteUrl = `features/${route.params.pageSlug}/${route.params.pageSubSlug}`
const pageOneResponse = await fetchWithHandleRedirect(absoluteUrl, route)

pageRef.value = pageOneResponse.page
// TODO: 型修正
pagePartsRef.value = pageOneResponse.page_parts as PagePartsSetType[]

const productCategoryVariables = reactive<PublicProductCategoryFindOneQueryVariables>({
  filter: {
    document_id: "",
  },
})

const { executeQuery: executeQueryProductCategoryFindOne } = await usePublicProductCategoryFindOneQuery({
  requestPolicy: "cache-first",
  variables: productCategoryVariables,
  pause: true,
})

const loadChildParts = (): void => {
  const tempPartsList: typeof pagePartsRef.value = []
  for (const pageParts of pagePartsRef.value) {
    // タブグループ内のパーツは childPartsList に格納する
    if (pageParts.child_page_parts_ids && pageParts.child_page_parts_ids?.length > 0) {
      childPartsIds.value.push(...pageParts.child_page_parts_ids)
      tempPartsList.push({
        ...pageParts,
        childPartsList: pagePartsRef.value.filter((parts) =>
          pageParts.child_page_parts_ids?.includes(parts.document_id),
        ),
      })
    } else {
      tempPartsList.push(pageParts)
    }
  }
  pagePartsRef.value = tempPartsList
}

const { marketingTagsEvent } = useSocial()
// page取得後にpagePartsの取得
if (pageRef.value) {
  await useAsyncData(
    "usePublicPagePartsManyQuery",
    async () => {
      isLoading.value = true

      const { setProducts } = useFetchProductForPageParts()
      await setProducts(pagePartsRef.value)
      loadChildParts()
      marketingTagsEvent(pagePartsRef.value)

      // fetch page parts product
      if (pagePartsRef.value.length > 0) {
        for await (const pageParts of pagePartsRef.value) {
          if (pageParts.category_id) {
            // fetch category
            productCategoryVariables.filter = {
              document_id: pageParts.category_id,
              is_status: true,
            }

            if (executeQueryProductCategoryFindOne().isPaused) await executeQueryProductCategoryFindOne().resume()
            const { data } = await executeQueryProductCategoryFindOne()

            if (data?.value?.publicProductCategoryFindOne) {
              pageParts.category = data?.value?.publicProductCategoryFindOne as ProductCategory
            }
          }
        }
      }

      isLoading.value = false
    },
    { lazy: true },
  )
}

// meta script
const script: MetaCustomType["script"] = []
const felmatTag = { src: "https://js.crossees.com/csslp.js" }
const linkATag = { src: "https://link-ag.net/dist/p/l/index.js" }
script.push(felmatTag)
script.push(linkATag)

const metaData = computed(() => {
  const title = pageRef.value?.title || ""
  const description = pageRef.value?.description || ""
  const image = pageRef.value?.ogp_url || ""
  return {
    title,
    description,
    image,
  }
})
const metaScript = computed(() => {
  const script: (ScriptPropertyText | ScriptPropertySrc)[] = []
  const felmatTag = { src: "https://js.crossees.com/csslp.js" }
  const linkATag = { src: "https://link-ag.net/dist/p/l/index.js" }
  script.push(felmatTag)
  script.push(linkATag)
  return script
})

useHead(
  customMeta.fetch({
    title: `${metaData.value.title}`,
    description: `${metaData.value.description}`,
    image: `${metaData.value.image}`,
    script: metaScript.value,
    robots: pageRef.value?.noindex ? "noindex" : undefined,
    jsonLDInfo: createArticleJsonLD({
      headline: pageRef.value?.title,
      image: pageRef.value?.ogp_url,
      description: pageRef.value?.description,
    }),
  }),
)
</script>
